import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AOS from 'aos';
import PublicNav from '../components/dna/PublicNav';
import Footer from '../components/dna/Footer';
import { fetchPublicArtistProfile } from '../services/artist.service';
import { handleImageError } from '../utils/imageUtils';
import '../styles/pages/artist-profile.css';
import 'aos/dist/aos.css';

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
    return null;
  }

  const total = Number(seconds);
  const minutes = Math.floor(total / 60);
  const remaining = Math.floor(total % 60);
  return `${minutes}:${String(remaining).padStart(2, '0')}`;
};

const formatDate = (value) => {
  if (!value) return null;
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(new Date(value));
};

const formatPrice = (release) => {
  if (release.is_free || Number(release.price_ariary || 0) === 0) {
    return 'Gratuit';
  }

  return `${Number(release.price_ariary || 0).toLocaleString()} Ar`;
};

const normalizeExternalUrl = (value, platform) => {
  if (!value) return null;

  const raw = String(value).trim();
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  const cleaned = raw.replace(/^@/, '').replace(/^\/+/, '');
  if (cleaned.includes('.')) {
    return `https://${cleaned}`;
  }

  if (platform === 'instagram') {
    return `https://instagram.com/${cleaned}`;
  }

  if (platform === 'facebook') {
    return `https://facebook.com/${cleaned}`;
  }

  if (platform === 'youtube') {
    return `https://youtube.com/${cleaned}`;
  }

  return `https://${cleaned}`;
};

const ArtistProfile = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    AOS.init({ duration: 700, once: true });
  }, []);

  useEffect(() => {
    let active = true;

    const loadProfile = async () => {
      setLoading(true);
      setError('');

      try {
        const result = await fetchPublicArtistProfile(slug);
        if (!active) return;
        setData(result);
      } catch (err) {
        if (!active) return;
        setError(err.message || 'Artiste introuvable.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      active = false;
    };
  }, [slug]);

  const artist = data?.artist;
  const releases = data?.releases || [];
  const stats = data?.stats || {};

  const socialLinks = [
    {
      label: 'Site web',
      iconClass: 'fa-solid fa-globe',
      url: normalizeExternalUrl(artist?.website, 'website')
    },
    {
      label: 'Instagram',
      iconClass: 'fa-brands fa-instagram',
      url: normalizeExternalUrl(artist?.instagram, 'instagram')
    },
    {
      label: 'Facebook',
      iconClass: 'fa-brands fa-facebook',
      url: normalizeExternalUrl(artist?.facebook, 'facebook')
    },
    {
      label: 'YouTube',
      iconClass: 'fa-brands fa-youtube',
      url: normalizeExternalUrl(artist?.youtube, 'youtube')
    }
  ].filter((item) => item.url);

  if (loading) {
    return (
      <div className="public-profile">
        <PublicNav />
        <main className="artist-profile-shell">
          <div className="artist-empty-state" data-aos="fade-up">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3">Chargement du profil artiste...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !artist) {
    return (
      <div className="public-profile">
        <PublicNav />
        <main className="artist-profile-shell">
          <section className="artist-error-state" data-aos="fade-up">
            <h3>Profil introuvable</h3>
            <p>{error || 'Cet artiste ne semble pas exister.'}</p>
            <div className="artist-release-actions justify-content-center mt-4">
              <Link to="/login" className="artist-release-link">
                Accéder à Pass&apos;io
              </Link>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="public-profile">
      <PublicNav />
      <main className="artist-profile-shell">
        <section className="artist-hero" data-aos="fade-up">
          <div className="artist-hero-grid">
            <div className="artist-portrait">
              <img
                src={
                  artist.profile_picture_url ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(artist.name || slug)}&background=5b0000&color=ffffff&size=512`
                }
                alt={artist.name}
                onError={handleImageError}
              />
            </div>

            <div
              className="artist-hero-copy"
              style={artist?.banner_url ? {
                backgroundImage: `linear-gradient(135deg, rgba(255,255,255,0.82), rgba(255,255,255,0.92)), url(${artist.banner_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: '24px',
                padding: '1.25rem'
              } : undefined}
            >
              <p className="eyebrow">Profil public</p>
              <h1>{artist.name}</h1>
              <p className="slug">@{artist.slug}</p>
              <p className="bio">
                {artist.bio || 'Ce profil public présente les sorties publiées de cet artiste et permet aux fans d’écouter un extrait de chaque release.'}
              </p>

              {socialLinks.length > 0 && (
                <div className="artist-social-links">
                  {socialLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      className="artist-social-link"
                    >
                      <i className={link.iconClass} />
                      <span>{link.label}</span>
                    </a>
                  ))}
                </div>
              )}

              <div className="artist-stats-grid">
                <div className="artist-stat-card">
                  <span className="label">Releases</span>
                  <span className="value">{stats.releases_total ?? 0}</span>
                </div>
                <div className="artist-stat-card">
                  <span className="label">Pistes</span>
                  <span className="value">{stats.tracks_total ?? 0}</span>
                </div>
                <div className="artist-stat-card">
                  <span className="label">Fans</span>
                  <span className="value">{stats.fans_total ?? 0}</span>
                </div>
                <div className="artist-stat-card">
                  <span className="label">Ventes</span>
                  <span className="value">{stats.sales_total ?? 0}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="artist-section" data-aos="fade-up">
          <div className="artist-section-header">
            <div>
              <h2>Releases publiées</h2>
              <p>{releases.length ? `${releases.length} sortie(s) disponible(s)` : 'Aucune release publiée pour le moment.'}</p>
            </div>
          </div>

          {releases.length === 0 ? (
            <div className="artist-empty-state">
              <h3>Pas encore de sortie publique</h3>
              <p>Reviens un peu plus tard pour découvrir les prochaines releases de cet artiste.</p>
            </div>
          ) : (
            <div className="artist-release-grid">
              {releases.map((release, releaseIndex) => (
                <article className="artist-release-card" key={release.id} data-aos="fade-up" data-aos-delay={releaseIndex * 80}>
                  <div className="artist-release-cover">
                    <img
                      src={
                        release.cover_url ||
                        `https://ui-avatars.com/api/?name=${encodeURIComponent(release.title || 'Release')}&background=5b0000&color=ffffff&size=512`
                      }
                      alt={release.title}
                      onError={handleImageError}
                    />
                    <span className="type-badge">
                      {release.type === 'single' ? 'Single' : release.type === 'ep' ? 'EP' : 'Album'}
                    </span>
                  </div>

                  <div className="artist-release-body">
                    <div className="artist-release-title-row">
                      <div>
                        <h3>{release.title}</h3>
                        <div className="artist-release-meta">
                          <span>{formatDate(release.created_at) || 'Date inconnue'}</span>
                          <span>{release.track_count || release.tracks?.length || 0} piste(s)</span>
                          <span>{formatPrice(release)}</span>
                        </div>
                      </div>
                    </div>

                    <p className="artist-release-description">
                      {release.description || 'Aucune description pour cette release.'}
                    </p>

                    {(release.tracks || []).length > 0 && (
                    <div className="artist-track-list">
                      {release.tracks.map((track) => (
                        <div className="artist-track-item" key={track.id}>
                          <div className="artist-track-topline">
                            <div className="artist-track-name">
                              {track.position ? `${track.position}. ` : ''}
                              {track.title}
                            </div>
                            {formatDuration(track.duration) && (
                              <div className="artist-track-duration">
                                {formatDuration(track.duration)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    )}

                    <div className="artist-mobile-hint">
                      <i className="fa-solid fa-mobile-screen" />
                      <span>Écoute sur l&apos;app</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ArtistProfile;
